<template>
  <section
    v-if="featureEnabled"
    class="prmdt"
    data-testid="pay-range-market-data-table"
    :aria-label="`Market data from ${sources.length} sources`"
  >
    <h3 class="prmdt__heading" data-testid="prmdt-heading">{{ headingText }}</h3>

    <div class="prmdt__container">
      <table class="prmdt__table">
        <caption class="prmdt__visually-hidden">
          Market data sources consulted for pay range
        </caption>
        <thead>
          <tr>
            <th scope="col" class="prmdt__header prmdt__header--source">Source</th>
            <th scope="col" class="prmdt__header prmdt__header--num">Min</th>
            <th scope="col" class="prmdt__header prmdt__header--num">Median</th>
            <th scope="col" class="prmdt__header prmdt__header--num">Max</th>
            <th scope="col" class="prmdt__header">Data as of</th>
            <th v-if="hasScopeLevel" scope="col" class="prmdt__header">Scope</th>
            <th scope="col" class="prmdt__header prmdt__header--status">Status</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(source, index) in sources"
            :key="`source-${index}`"
            class="prmdt__row"
            :class="{ 'prmdt__row--outlier': isIncluded(source) === false }"
            :data-testid="`source-row-${index}`"
          >
            <th scope="row" class="prmdt__cell prmdt__cell--source">{{ source.name }}</th>
            <td class="prmdt__cell prmdt__cell--num">{{ formatAmount(source.min) }}</td>
            <td class="prmdt__cell prmdt__cell--num">{{ formatAmount(source.median) }}</td>
            <td class="prmdt__cell prmdt__cell--num">{{ formatAmount(source.max) }}</td>
            <td class="prmdt__cell">{{ formatAsOf(source.asOf) }}</td>
            <td v-if="hasScopeLevel" class="prmdt__cell" :data-testid="`scope-${index}`">
              {{ scopeLevelLabel(source.scopeLevel) }}
            </td>
            <td class="prmdt__cell prmdt__cell--status">
              <span
                class="prmdt__badge"
                :class="isIncluded(source) ? 'prmdt__badge--included' : 'prmdt__badge--outlier'"
                :data-testid="`status-badge-${index}`"
              >
                {{ isIncluded(source) ? 'Included' : 'Outlier' }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref } from "vue";
import { LD_FLAG_IDEA_MDP_429 } from "@components/constants/launchDarklyFlagKeys";
import { formatPayRangeWholeAmount } from "@components/utils/formatPayRangeAmount";

const props = defineProps({
  /**
   * Market data sources consulted for the pay range suggestion.
   * Each source: { name, min, median, max, asOf ('YYYY-MM' or display text), status ('included' | 'outlier' | 'excluded'), scopeLevel? }
   * An 'excluded' status (as returned by the API) is shown as an Outlier.
   */
  sources: {
    type: Array,
    required: true,
    default: () => [],
    validator: (value) => {
      return Array.isArray(value) && value.every(s =>
        typeof s.name === 'string' &&
        typeof s.min === 'number' &&
        (s.median == null || typeof s.median === 'number') &&
        typeof s.max === 'number' &&
        ['included', 'outlier', 'excluded'].includes(s.status)
      );
    }
  },
  /**
   * Currency code for formatting amounts (e.g., "USD").
   */
  currency: {
    type: String,
    required: true,
    default: 'USD'
  }
});

const proxy = getCurrentInstance()?.proxy;

const SCOPE_LEVEL_LABELS = {
  COUNTRY: "Country-wide",
  REGION: "Region",
  SUB_REGION: "Sub-region",
  GLOBAL: "Global",
};

const hasScopeLevel = computed(() => props.sources.some((s) => s.scopeLevel));

function scopeLevelLabel(value) {
  if (!value) return "—";
  return SCOPE_LEVEL_LABELS[String(value).toUpperCase()] || String(value);
}

/**
 * MDP-429 feature flag — Fair Pay MVP idea.
 * Component renders by default; hidden when LD_FLAG_IDEA_MDP_429 resolves to false.
 */
const featureEnabled = ref(true);

async function resolveFeatureFlag() {
  if (typeof proxy?.$getFlag === "function") {
    const enabled = await proxy.$getFlag(LD_FLAG_IDEA_MDP_429, true);
    if (enabled === false) {
      featureEnabled.value = false;
    }
  }
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const headingText = computed(
  () => `MARKET DATA — ${props.sources.length} ${props.sources.length === 1 ? "SOURCE" : "SOURCES"} CONSULTED`
);

function isIncluded(source) {
  return source.status === "included";
}

/** Whole units in the range's currency, e.g. "RON 6,861". */
function formatAmount(value) {
  if (value == null || value === "") return "—";
  return formatPayRangeWholeAmount(value, props.currency);
}

/** "2026-08" → "Aug 2026"; anything else is shown as given. */
function formatAsOf(value) {
  if (value == null || value === "") return "—";
  const match = /^(\d{4})-(\d{2})(?:-\d{2})?$/.exec(String(value));
  const month = match ? MONTHS[Number(match[2]) - 1] : null;
  return month ? `${month} ${match[1]}` : String(value);
}

onMounted(async () => {
  await resolveFeatureFlag();
});
</script>

<style scoped>
.prmdt {
  display: block;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
}

.prmdt__heading {
  margin: 0 0 8px 0;
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 12px;
  line-height: 18px;
  letter-spacing: 0.04em;
  color: #475467;
}

.prmdt__container {
  border: 1px solid #eaecf0;
  border-radius: 12px;
  background: #ffffff;
  overflow-x: auto;
}

.prmdt__table {
  width: 100%;
  border-collapse: collapse;
  border-spacing: 0;
}

.prmdt__visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.prmdt__header {
  height: 36px;
  padding: 8px 14px;
  background: #f9fafb;
  border-bottom: 1px solid #eaecf0;
  font-family: Inter, sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
  color: #475467;
  text-align: left;
}

.prmdt__header--num {
  text-align: right;
}

.prmdt__cell {
  height: 44px;
  padding: 10px 14px;
  background: #ffffff;
  border-bottom: 1px solid #eaecf0;
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #101828;
  text-align: left;
}

.prmdt__cell--source {
  font-weight: 500;
}

.prmdt__cell--num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.prmdt__row:last-child .prmdt__cell {
  border-bottom: 0;
}

.prmdt__row--outlier .prmdt__cell {
  background: #f9fafb;
  color: #667085;
}

.prmdt__row--outlier .prmdt__cell:not(.prmdt__cell--status) {
  text-decoration: line-through;
}

/* Status badges */
.prmdt__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  padding: 2px 8px;
  border-radius: 999px;
  font-family: Inter, sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
  box-sizing: border-box;
}

.prmdt__badge--included {
  background: #ecfdf3;
  border: 1px solid #abefc6;
  color: #067647;
}

.prmdt__badge--outlier {
  background: #fffaeb;
  border: 1px solid #fedf89;
  color: #b54708;
}
</style>
